import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppShell } from "../components/cod/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { plansApi, tasksApi, docsApi, apptApi, msgApi, notifApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CheckCircle2, Circle, Upload, FileText, Calendar, Bell, MessageSquare, Plus, ArrowRight, Sparkles, TrendingUp, Download, Eye, Trash2, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { IMG } from "../lib/images";
import { ChicagoStars, CODLogoSVG } from "../components/cod/Logo";

export function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [appts, setAppts] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [talentProfile, setTalentProfile] = useState(null);

  useEffect(() => {
    plansApi.active().then(setPlan).catch(()=>{});
    tasksApi.mine().then(setTasks).catch(()=>{});
    apptApi.mine().then(setAppts).catch(()=>{});
    notifApi.mine().then(setNotifs).catch(()=>{});
    import("../lib/api").then(m => m.default.get("/participants/me/profile")
      .then(r => setTalentProfile(r.data || {})).catch(()=>{}));
  }, []);

  const openTasks = tasks.filter(t => t.status === "open");
  const upcoming = appts.filter(a => new Date(a.starts_at) > new Date()).slice(0,3);
  const unread = notifs.filter(n => !n.read);

  return (
    <AppShell>
      <div className="space-y-8">
        {/* BRIGHT welcome banner */}
        <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
          <div className="cod-stripe"/>
          <div className="grid md:grid-cols-12">
            <div className="md:col-span-8 p-8 sm:p-10">
              <p className="cod-eyebrow">Welcome back</p>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-2">{user?.full_name?.split(" ")[0]}</h1>
              <div className="cod-rule-red mt-4"/>
              <p className="text-slate-700 mt-4 max-w-md">Here's where things stand today. Something will move forward.</p>
              <Button size="lg" onClick={()=>navigate("/app/navigator")} className="mt-6 bg-[var(--cod-red)] hover:bg-[#A40D26] text-white" data-testid="home-talk-navigator">
                <MessageSquare size={16} className="mr-2"/> Talk with Navigator
              </Button>
            </div>
            <div className="md:col-span-4 hidden md:block">
              <img src={IMG.careerCoaching} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover"/>
            </div>
          </div>
        </div>

        {/* Candidate Profile — Employment & Staffing quick link */}
        {(() => {
          const ready = talentProfile?.talent_status === "ready_for_review";
          const hasAny = ready || !!(talentProfile?.skills?.length || talentProfile?.positions_of_interest?.length);
          return (
            <Card data-testid="talent-profile-card" className="border-l-4" style={{borderLeftColor:"var(--cod-red)"}}>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-serif text-xl">Candidate Profile</CardTitle>
                  <p className="text-sm text-slate-600 mt-1">
                    {ready
                      ? "Submitted — COD staff have been notified. You can still update your profile any time."
                      : hasAny
                        ? "Draft saved. Add details or submit for review when you're ready."
                        : "Tell COD what you're looking for so we can match you to opportunities."}
                  </p>
                </div>
                <Badge className="text-xs" style={ready
                    ? {background:"#DCFCE7", color:"#166534"}
                    : {background:"#FEF3C7", color:"#92400E"}}>
                  {ready ? "Submitted" : (hasAny ? "Draft" : "Not started")}
                </Badge>
              </CardHeader>
              <CardContent>
                <Button onClick={()=>navigate("/app/talent-profile")} className="cod-bg-red" data-testid="talent-profile-open">
                  {hasAny ? "Update Candidate Profile" : "Complete Candidate Profile"}
                </Button>
              </CardContent>
            </Card>
          );
        })()}

        <div className="grid md:grid-cols-3 gap-6">
          <Card className="md:col-span-2" data-testid="card-current-plan">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-serif text-xl">Your Opportunity Plan</CardTitle>
              <Badge variant="outline">{plan?.id ? `v${plan.version}` : "Not started"}</Badge>
            </CardHeader>
            <CardContent>
              {plan?.data ? (
                <div className="space-y-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Goal</div>
                    <div className="text-lg text-slate-900 font-medium">{plan.data.goal}</div>
                  </div>
                  {plan.data.barriers?.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Barriers we're addressing</div>
                      <div className="flex flex-wrap gap-2">{plan.data.barriers.map((b,i)=><Badge key={`${b}-${i}`} variant="secondary">{b}</Badge>)}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Next step</div>
                    <div className="text-slate-800">{plan.data.next_step_for_participant}</div>
                  </div>
                  <Button variant="link" onClick={()=>navigate("/app/plan")} className="px-0" data-testid="home-view-plan">
                    View full plan <ArrowRight size={14} className="ml-1"/>
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Sparkles size={32} className="mx-auto mb-3" style={{ color: "var(--cod-navy)" }}/>
                  <p className="text-slate-600 mb-4">You don't have a plan yet. Start a conversation with the Navigator.</p>
                  <Button onClick={()=>navigate("/app/navigator")} style={{ background: "var(--cod-navy)" }} data-testid="home-start-conversation">Start</Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="card-progress" className="overflow-hidden">
            <div className="h-2 cod-stripe"/>
            <CardHeader>
              <CardTitle className="font-serif text-xl flex items-center gap-2"><TrendingUp size={18} style={{color:"var(--cod-red)"}}/> Opportunity Readiness</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-2">
                <div className="font-serif text-3xl" style={{ color: "var(--cod-navy)" }}>
                  {plan ? "Almost Ready" : "Just Starting"}
                </div>
                {/* Progress bar */}
                <div className="mt-4 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: plan ? `${Math.min(95, 30 + (tasks.filter(t=>t.status==="done").length / Math.max(1,tasks.length))*65)}%` : "12%", background: "var(--cod-red)" }}/>
                </div>
                <p className="text-sm text-slate-600 mt-3">
                  {plan
                    ? `${openTasks.length} task${openTasks.length===1?"":"s"} remaining`
                    : "Begin with the Navigator to get started"}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <Card data-testid="card-tasks">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-serif text-lg">Tasks</CardTitle>
              <Button variant="ghost" size="sm" onClick={()=>navigate("/app/tasks")}>View all</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {openTasks.slice(0,4).map(t => (
                <div key={t.id} className="flex items-start gap-2 text-sm">
                  <Circle size={16} className="mt-0.5 text-slate-400"/>
                  <div><div className="text-slate-800">{t.title}</div><div className="text-xs text-slate-500">{t.due_date}</div></div>
                </div>
              ))}
              {openTasks.length === 0 && <p className="text-sm text-slate-500">No tasks yet.</p>}
            </CardContent>
          </Card>

          <Card data-testid="card-appointments">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-serif text-lg">Upcoming</CardTitle>
              <Button variant="ghost" size="sm" onClick={()=>navigate("/app/appointments")}>View all</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {upcoming.map(a => (
                <div key={a.id} className="text-sm">
                  <div className="text-slate-800">{a.title}</div>
                  <div className="text-xs text-slate-500">{new Date(a.starts_at).toLocaleString()}</div>
                </div>
              ))}
              {upcoming.length === 0 && <p className="text-sm text-slate-500">No appointments scheduled.</p>}
            </CardContent>
          </Card>

          <Card data-testid="card-notifications">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-serif text-lg">Notifications</CardTitle>
              {unread.length > 0 && <Badge style={{ background: "var(--cod-red)" }}>{unread.length} new</Badge>}
            </CardHeader>
            <CardContent className="space-y-2">
              {notifs.slice(0,4).map(n => (
                <div key={n.id} className="text-sm flex items-start gap-2">
                  <Bell size={14} className="mt-0.5 text-slate-400"/>
                  <div>
                    <div className={`${!n.read ? "font-medium" : ""} text-slate-800`}>{n.title}</div>
                    <div className="text-xs text-slate-500">{n.body}</div>
                  </div>
                </div>
              ))}
              {notifs.length === 0 && <p className="text-sm text-slate-500">No notifications.</p>}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

export function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState({ title:"", description:"", priority:"medium" });
  const reload = () => tasksApi.mine().then(setTasks);
  useEffect(()=>{ reload(); }, []);
  const toggle = async (t) => {
    await tasksApi.update(t.id, { status: t.status === "open" ? "done" : "open" });
    reload();
  };
  const create = async () => {
    if(!newTask.title) return;
    await tasksApi.create(newTask);
    toast.success("Task added", { icon: <CODLogoSVG size={22}/> });
    setNewTask({ title:"", description:"", priority:"medium" });
    reload();
  };
  return (
    <AppShell>
      <div className="space-y-6">
        <h1 className="font-serif text-3xl text-slate-900">Tasks</h1>
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-2">
              <Input placeholder="Add a task..." value={newTask.title} onChange={e=>setNewTask({...newTask, title:e.target.value})} data-testid="task-input"/>
              <Button onClick={create} style={{ background: "var(--cod-navy)" }} data-testid="task-add"><Plus size={16}/></Button>
            </div>
          </CardContent>
        </Card>
        <div className="space-y-2">
          {tasks.map(t => (
            <Card key={t.id} data-testid={`task-${t.id}`}>
              <CardContent className="py-4 flex items-start gap-3">
                <button onClick={()=>toggle(t)} className="mt-0.5" data-testid={`task-toggle-${t.id}`}>
                  {t.status === "done" ? <CheckCircle2 size={20} className="text-emerald-600"/> : <Circle size={20} className="text-slate-400"/>}
                </button>
                <div className="flex-1">
                  <div className={t.status==="done" ? "line-through text-slate-500" : "text-slate-900"}>{t.title}</div>
                  {t.description && <div className="text-sm text-slate-600 mt-1">{t.description}</div>}
                  <div className="flex gap-2 mt-2">
                    {t.due_date && <Badge variant="outline">{t.due_date}</Badge>}
                    <Badge variant={t.priority==="high"?"destructive":"secondary"}>{t.priority}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {tasks.length === 0 && <p className="text-sm text-slate-500">No tasks yet. Tasks are created automatically from your Opportunity Plan, or add your own above.</p>}
        </div>
      </div>
    </AppShell>
  );
}

export function PlanPage() {
  const [plans, setPlans] = useState([]);
  useEffect(()=>{ plansApi.mine().then(setPlans); }, []);
  const active = plans.find(p => p.active);
  const history = plans.filter(p => !p.active);
  return (
    <AppShell>
      <div className="space-y-6">
        <h1 className="font-serif text-3xl text-slate-900">My Opportunity Plan</h1>
        {!active && <p className="text-slate-600">You don't have an active plan yet. Talk with the Navigator to build one.</p>}
        {active && (
          <Card data-testid="active-plan">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="font-serif text-2xl">{active.data.goal}</CardTitle>
                  <p className="text-sm text-slate-500 mt-1">Version {active.version} · Living roadmap</p>
                </div>
                <Badge>Active</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-serif text-lg mb-2">About your situation</h3>
                <p className="text-slate-700 leading-relaxed">{active.data.tone_summary}</p>
              </div>
              <div>
                <h3 className="font-serif text-lg mb-2">Barriers we're addressing</h3>
                <div className="flex flex-wrap gap-2">{(active.data.barriers||[]).map((b,i)=><Badge key={`${b}-${i}`} variant="secondary">{b}</Badge>)}</div>
              </div>
              <div>
                <h3 className="font-serif text-lg mb-2">Action steps</h3>
                <ol className="space-y-2">
                  {(active.data.action_steps||[]).map((s,i)=>(
                    <li key={`${s.title||"step"}-${i}`} className="border-l-2 pl-3 py-1" style={{ borderColor: "var(--cod-navy)" }}>
                      <div className="font-medium text-slate-900">{s.title}</div>
                      <div className="text-xs text-slate-500">Owner: {s.owner} · Due: {s.due}</div>
                    </li>
                  ))}
                </ol>
              </div>
              {active.data.milestones?.length > 0 && (
                <div>
                  <h3 className="font-serif text-lg mb-2">Milestones</h3>
                  <ul className="list-disc list-inside text-slate-700">{active.data.milestones.map((m,i)=><li key={`${m}-${i}`}>{m}</li>)}</ul>
                </div>
              )}
              {active.data.recommended_opportunities?.length > 0 && (
                <div>
                  <h3 className="font-serif text-lg mb-2">Recommended opportunities</h3>
                  <ul className="list-disc list-inside text-slate-700">{active.data.recommended_opportunities.map((m,i)=><li key={`${m}-${i}`}>{m}</li>)}</ul>
                </div>
              )}
              <div className="p-4 rounded-lg" style={{ background: "rgba(30,58,138,0.06)" }}>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Your next step</div>
                <div className="font-medium text-slate-900">{active.data.next_step_for_participant}</div>
              </div>
              <Button variant="outline" onClick={()=>window.print()} data-testid="plan-print">Print plan</Button>
            </CardContent>
          </Card>
        )}
        {history.length > 0 && (
          <div>
            <h2 className="font-serif text-xl text-slate-900 mb-3">Plan history</h2>
            {history.map(p => (
              <Card key={p.id} className="mb-3">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div><span className="font-medium">v{p.version}</span> · {p.data.goal}</div>
                    <Badge variant="outline">{new Date(p.created_at).toLocaleDateString()}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

export function DocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [replaceFor, setReplaceFor] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const { user } = useAuth();
  const reload = () => docsApi.mine().then(setDocs).catch(()=>{});
  useEffect(()=>{
    reload();
    docsApi.categories().then(d => setCats(d.categories || [])).catch(()=>{});
  }, []);

  const allowedCats = cats.filter(c => !c.staff_only || (user && user.role !== "participant"));

  const handleUpload = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    setUploading(true);
    try {
      await docsApi.upload(form);
      toast.success("Document uploaded");
      setOpen(false); reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleReplace = async (e) => {
    e.preventDefault();
    if (!replaceFor) return;
    const form = new FormData(e.target);
    setUploading(true);
    try {
      await docsApi.replace(replaceFor.id, form);
      toast.success("New version uploaded");
      setReplaceFor(null); reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Replace failed");
    } finally { setUploading(false); }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? This cannot be undone.`)) return;
    try {
      await docsApi.remove(d.id);
      toast.success("Document deleted");
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const openPreview = async (d) => {
    setPreviewDoc(d); setPreviewUrl("");
    try {
      const blob = await docsApi.fetchBlob(d.id, true);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch { toast.error("Preview failed"); }
  };
  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewDoc(null); setPreviewUrl("");
  };

  const visibleDocs = docs.filter(d => !d.is_deleted && d.status !== "superseded");
  const byCat = (key) => visibleDocs.filter(d => d.category === key);
  const catLabel = (key) => (cats.find(c => c.key === key)?.label) || key;

  // Group categories for display tabs: all in one list; show only those with docs or that are allowed
  const tabs = allowedCats;
  const defaultTab = tabs[0]?.key || "resume";

  return (
    <AppShell>
      <div className="space-y-6" data-testid="documents-page">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="font-serif text-3xl text-slate-900">Document Vault</h1>
            <p className="text-sm text-slate-600 mt-1">Secure storage for resumes, identification, certifications, licenses, and program documents.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button style={{ background: "var(--cod-navy)" }} data-testid="upload-btn">
                <Upload size={16} className="mr-2"/> Upload
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload a document</DialogTitle>
                <DialogDescription>Add a file to your secure Document Vault. Max 25MB. Accepted: PDF, images, Word, text.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4">
                <div><Label htmlFor="doc-name">Name</Label><Input id="doc-name" name="name" required data-testid="upload-name"/></div>
                <div>
                  <Label htmlFor="doc-cat">Category</Label>
                  <select id="doc-cat" name="category" required className="w-full border rounded-md p-2 bg-white" data-testid="upload-category">
                    {allowedCats.map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div><Label htmlFor="doc-file">File</Label><Input id="doc-file" type="file" name="file" required data-testid="upload-file"/></div>
                <Button type="submit" disabled={uploading} style={{ background: "var(--cod-navy)" }} data-testid="upload-submit">
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {visibleDocs.length === 0 ? (
          <Card><CardContent className="py-10 text-center">
            <FileText size={32} className="mx-auto text-slate-300 mb-3"/>
            <p className="text-slate-600">No documents yet. Click <strong>Upload</strong> to get started.</p>
          </CardContent></Card>
        ) : (
          <Tabs defaultValue={defaultTab}>
            <TabsList className="flex-wrap h-auto">
              {tabs.map(c => (
                <TabsTrigger key={c.key} value={c.key} data-testid={`tab-${c.key}`}>
                  {c.label} {byCat(c.key).length > 0 && <span className="ml-1.5 text-xs opacity-70">({byCat(c.key).length})</span>}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabs.map(c => (
              <TabsContent key={c.key} value={c.key} className="space-y-2 mt-4">
                {byCat(c.key).map(d => (
                  <DocRow key={d.id} d={d} onPreview={() => openPreview(d)}
                    onReplace={() => setReplaceFor(d)}
                    onDelete={() => handleDelete(d)}
                    catLabel={catLabel}/>
                ))}
                {byCat(c.key).length === 0 && (
                  <p className="text-sm text-slate-500 px-1">No documents in <em>{c.label}</em>.</p>
                )}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>

      {/* Replace (new version) dialog */}
      <Dialog open={!!replaceFor} onOpenChange={(o)=>{ if(!o) setReplaceFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload a new version</DialogTitle>
            <DialogDescription>Replacing <strong>{replaceFor?.name}</strong>. Prior versions are preserved in history.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReplace} className="space-y-4">
            <div><Label htmlFor="rep-name">Name (optional)</Label>
              <Input id="rep-name" name="name" defaultValue={replaceFor?.name} data-testid="replace-name"/>
            </div>
            <div><Label htmlFor="rep-file">New file</Label>
              <Input id="rep-file" type="file" name="file" required data-testid="replace-file"/>
            </div>
            <Button type="submit" disabled={uploading} style={{ background: "var(--cod-navy)" }} data-testid="replace-submit">
              {uploading ? "Uploading..." : "Upload new version"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview modal */}
      <Dialog open={!!previewDoc} onOpenChange={(o)=>{ if(!o) closePreview(); }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewDoc?.name}</DialogTitle>
            <DialogDescription>{previewDoc?.filename} · v{previewDoc?.version || 1}</DialogDescription>
          </DialogHeader>
          <div className="h-[70vh] bg-slate-50 border rounded-md overflow-hidden flex items-center justify-center" data-testid="preview-frame">
            {!previewUrl ? (
              <p className="text-slate-500">Loading preview…</p>
            ) : previewDoc?.content_type?.startsWith("image/") ? (
              <img src={previewUrl} alt={previewDoc?.name} className="max-w-full max-h-full object-contain"/>
            ) : (
              <iframe src={previewUrl} title={previewDoc?.name} className="w-full h-full" />
            )}
          </div>
          {previewDoc && (
            <div className="flex justify-end">
              <a href={previewUrl} download={previewDoc.filename}
                 className="text-sm font-medium" style={{ color: "var(--cod-navy)" }}>
                <Download size={14} className="inline mr-1"/> Download
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function DocRow({ d, onPreview, onReplace, onDelete, catLabel }) {
  const previewable = d.content_type?.startsWith("image/") || d.content_type === "application/pdf";
  return (
    <Card data-testid={`doc-row-${d.id}`}>
      <CardContent className="py-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <FileText size={20} className="text-slate-400 mt-0.5 flex-shrink-0"/>
          <div className="min-w-0">
            <div className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
              {d.name}
              {d.verified && (
                <Badge style={{background:"var(--cod-navy-soft)", color:"var(--cod-navy)"}}>
                  <ShieldCheck size={12} className="mr-1"/> Verified
                </Badge>
              )}
              {d.version > 1 && (
                <Badge variant="outline">v{d.version}</Badge>
              )}
            </div>
            <div className="text-xs text-slate-500 truncate">
              {catLabel(d.category)} · {d.filename} · {new Date(d.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {previewable && (
            <Button size="sm" variant="outline" onClick={onPreview} data-testid={`preview-${d.id}`}>
              <Eye size={14} className="mr-1"/> Preview
            </Button>
          )}
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${d.id}/download`}
             className="inline-flex items-center px-3 py-1.5 text-sm font-medium border rounded-md hover:bg-slate-50"
             style={{ color: "var(--cod-navy)", borderColor: "var(--cod-navy)" }}
             data-testid={`download-${d.id}`}>
            <Download size={14} className="mr-1"/> Download
          </a>
          <Button size="sm" variant="outline" onClick={onReplace} data-testid={`replace-${d.id}`}>
            <RefreshCw size={14} className="mr-1"/> Replace
          </Button>
          <Button size="sm" variant="outline" onClick={onDelete}
            className="text-red-600 hover:bg-red-50 border-red-200" data-testid={`delete-${d.id}`}>
            <Trash2 size={14} className="mr-1"/> Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function AppointmentsPage() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [open, setOpen] = useState(false);
  const [rescheduling, setRescheduling] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [busy, setBusy] = useState(false);
  const reload = () => apptApi.mine(showHistory).then(setAppts).catch(()=>{});
  useEffect(()=>{ reload(); }, [showHistory]);

  const toIso = (localValue) => new Date(localValue).toISOString();

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const form = Object.fromEntries(new FormData(e.target));
      await apptApi.create({ ...form, starts_at: toIso(form.starts_at) });
      toast.success("Appointment requested", { icon: <CODLogoSVG size={22}/> });
      setOpen(false); reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule");
    } finally { setBusy(false); }
  };

  const doReschedule = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const form = Object.fromEntries(new FormData(e.target));
      await apptApi.reschedule(rescheduling.id, { starts_at: toIso(form.starts_at), reason: form.reason });
      toast.success("Appointment rescheduled");
      setRescheduling(null); reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reschedule");
    } finally { setBusy(false); }
  };

  const doCancel = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const form = Object.fromEntries(new FormData(e.target));
      await apptApi.cancel(cancelling.id, { reason: form.reason || "" });
      toast.success("Appointment cancelled");
      setCancelling(null); reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel");
    } finally { setBusy(false); }
  };

  const statusColor = (s) => ({
    scheduled: "bg-emerald-100 text-emerald-800",
    rescheduled: "bg-blue-100 text-blue-800",
    cancelled: "bg-red-100 text-red-700",
    superseded: "bg-slate-100 text-slate-500",
  }[s] || "bg-slate-100 text-slate-700");

  const canModify = (a) => a.status === "scheduled" || a.status === "rescheduled";
  const isStaffView = user?.role === "staff" || user?.role === "admin" || user?.role === "super_admin";

  return (
    <AppShell>
      <div className="space-y-6" data-testid="appointments-page">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="font-serif text-3xl text-slate-900">Appointments</h1>
            <p className="text-sm text-slate-600 mt-1">{isStaffView ? "Manage your caseload's appointments." : "Your upcoming appointments with your Case Manager."}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-sm flex items-center gap-2 text-slate-600 cursor-pointer">
              <input type="checkbox" checked={showHistory} onChange={e=>setShowHistory(e.target.checked)} data-testid="show-history"/>
              Show history
            </label>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button style={{ background: "var(--cod-navy)" }} data-testid="appt-new"><Calendar size={16} className="mr-2"/> New</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{isStaffView ? "Schedule appointment" : "Request an appointment"}</DialogTitle>
                  <DialogDescription>{isStaffView ? "Book on behalf of a participant. They'll be notified automatically." : "Pick a time that works for you. Your Case Manager will confirm."}</DialogDescription>
                </DialogHeader>
                <form onSubmit={create} className="space-y-4">
                  <div><Label htmlFor="ap-title">Title</Label><Input id="ap-title" name="title" required data-testid="appt-title"/></div>
                  <div><Label htmlFor="ap-time">Date & Time</Label><Input id="ap-time" name="starts_at" type="datetime-local" required data-testid="appt-time"/></div>
                  {isStaffView && (
                    <div><Label htmlFor="ap-for">Participant user ID</Label><Input id="ap-for" name="for_user_id" placeholder="Optional — leave empty for personal appt" data-testid="appt-for-user"/></div>
                  )}
                  <div><Label htmlFor="ap-dur">Duration (minutes)</Label><Input id="ap-dur" name="duration_minutes" type="number" min="5" max="480" defaultValue="30" data-testid="appt-duration"/></div>
                  <div><Label htmlFor="ap-notes">Notes</Label><Textarea id="ap-notes" name="notes" data-testid="appt-notes"/></div>
                  <Button type="submit" disabled={busy} style={{ background: "var(--cod-navy)" }} data-testid="appt-submit">
                    {busy ? "Saving..." : (isStaffView ? "Schedule" : "Request")}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="space-y-2" data-testid="appt-list">
          {appts.length === 0 && <p className="text-sm text-slate-500">No appointments.</p>}
          {appts.map(a => (
            <Card key={a.id} data-testid={`appt-${a.id}`}>
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900">{a.title}</span>
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${statusColor(a.status)}`}>{a.status}</span>
                      {a.previous_id && <Badge variant="outline" className="text-xs">Rescheduled</Badge>}
                    </div>
                    <div className="text-sm text-slate-600 mt-1">{new Date(a.starts_at).toLocaleString()} · {a.duration_minutes || 30} min</div>
                    {a.notes && <div className="text-sm text-slate-500 mt-1">{a.notes}</div>}
                    {a.status === "cancelled" && a.cancel_reason && (
                      <div className="text-xs text-red-600 mt-1">Cancelled: {a.cancel_reason}</div>
                    )}
                  </div>
                  {canModify(a) && (
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={()=>setRescheduling(a)} data-testid={`reschedule-${a.id}`}>Reschedule</Button>
                      <Button size="sm" variant="outline" onClick={()=>setCancelling(a)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`cancel-${a.id}`}>Cancel</Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Reschedule dialog */}
      <Dialog open={!!rescheduling} onOpenChange={(o)=>{ if(!o) setRescheduling(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reschedule appointment</DialogTitle>
            <DialogDescription>Pick a new time. Rescheduling within 2 hours requires calling your Case Manager.</DialogDescription>
          </DialogHeader>
          <form onSubmit={doReschedule} className="space-y-4">
            <div><Label htmlFor="rs-time">New date & time</Label>
              <Input id="rs-time" name="starts_at" type="datetime-local" required data-testid="rs-time"/></div>
            <div><Label htmlFor="rs-reason">Reason (optional)</Label>
              <Input id="rs-reason" name="reason" data-testid="rs-reason"/></div>
            <Button type="submit" disabled={busy} style={{background:"var(--cod-navy)"}} data-testid="rs-submit">
              {busy ? "Saving..." : "Reschedule"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!cancelling} onOpenChange={(o)=>{ if(!o) setCancelling(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel appointment</DialogTitle>
            <DialogDescription>This will notify the other party. Cancellations within 2 hours require calling your Case Manager.</DialogDescription>
          </DialogHeader>
          <form onSubmit={doCancel} className="space-y-4">
            <div><Label htmlFor="cn-reason">Reason (optional)</Label>
              <Input id="cn-reason" name="reason" data-testid="cn-reason"/></div>
            <Button type="submit" disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cn-submit">
              {busy ? "Cancelling..." : "Cancel appointment"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

export function MessagesPage() {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const reload = () => msgApi.mine().then(setMsgs);
  useEffect(()=>{ reload(); }, []);
  return (
    <AppShell>
      <div className="space-y-6">
        <h1 className="font-serif text-3xl text-slate-900">Messages</h1>
        <div className="space-y-2">
          {msgs.map(m => (
            <Card key={m.id}>
              <CardContent className="py-4">
                <div className="text-xs text-slate-500 mb-1">
                  {m.from_user_id === user?.id ? "You" : "Case Manager"} · {new Date(m.created_at).toLocaleString()}
                  {m.from_navigator && <Badge variant="outline" className="ml-2">Navigator</Badge>}
                </div>
                <div className="text-slate-800">{m.body}</div>
              </CardContent>
            </Card>
          ))}
          {msgs.length === 0 && <p className="text-sm text-slate-500">No messages yet. Your assigned Case Manager will be in touch.</p>}
        </div>
      </div>
    </AppShell>
  );
}
